import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/** The two routes a modal can sit in front of. */
const MAIN_VIEWS = ['/', '/table'] as const

const STORAGE_KEY = 'pf:last-main-view'

function readLastMainView(): string {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored && (MAIN_VIEWS as readonly string[]).includes(stored)) return stored
  } catch {
    // Private mode / blocked storage — fall through to the dashboard.
  }
  return '/'
}

/**
 * Remembers which main view the user is on.
 *
 * Mounted once by the layout. sessionStorage rather than component state so
 * the answer survives the modal routes, which render their own layout rather
 * than sitting on top of the view they were opened from.
 */
export function useTrackMainView(): void {
  const { pathname } = useLocation()

  React.useEffect(() => {
    if (!(MAIN_VIEWS as readonly string[]).includes(pathname)) return
    try {
      sessionStorage.setItem(STORAGE_KEY, pathname)
    } catch {
      // Nothing to do — readLastMainView falls back to the dashboard.
    }
  }, [pathname])
}

/**
 * Closing a modal should put you back where you opened it.
 *
 * Modal routes used to `navigate('/')` on close, which silently teleported
 * anyone working in the Table view to the dashboard — open a transaction
 * list from a cell, close it, and you'd lost your place.
 */
export default function useCloseToOrigin(): () => void {
  const navigate = useNavigate()

  return React.useCallback(() => {
    navigate(readLastMainView())
  }, [navigate])
}
